import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ScrollArea } from "./ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Users,
  Store,
  UtensilsCrossed,
  MessageSquare,
  Plus,
  Share2,
  Trash2,
  Loader2,
  Search,
  X,
  UserPlus,
  List,
  FolderPlus,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Category Section Component
const CategorySection = ({ 
  category, 
  title, 
  icon: Icon, 
  onSelectUser, 
  onShareUser,
  platformDomain = "okchat.okgo.mx"
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchUsers();
  }, [category]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/api/user/saved/${category}`, {
        headers: { ...getAuthHeaders() },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error("Error fetching saved users:", err);
    }
    setLoading(false);
  };

  const removeUser = async (userId) => {
    try {
      const res = await fetch(`${API}/api/user/saved/${category}/${userId}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
        credentials: "include"
      });
      if (res.ok) {
        setUsers(users.filter(u => u.user_id !== userId));
        toast.success("Usuario eliminado");
      }
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const handleShare = async (user) => {
    const profileUrl = `https://${platformDomain}/u/${user.nickname}`;
    try {
      await navigator.clipboard.writeText(profileUrl);
      toast.success("Enlace copiado al portapapeles");
    } catch (err) {
      toast.error("Error al copiar enlace");
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.nickname?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b border-slate-700/50">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder={`Buscar en ${title}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-[#242f3d] border-transparent text-white placeholder:text-slate-500"
          />
        </div>
      </div>

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-red-500" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8 px-4">
            <Icon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No hay {title.toLowerCase()} guardados</p>
            <p className="text-slate-600 text-xs mt-1">Guarda usuarios desde el chat para verlos aquí</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredUsers.map(user => (
              <div 
                key={user.user_id}
                className="flex items-center p-3 rounded-lg hover:bg-[#232e3c] group transition-colors"
              >
                <Avatar 
                  className="w-10 h-10 cursor-pointer"
                  onClick={() => onSelectUser?.(user)}
                >
                  <AvatarImage src={user.picture} />
                  <AvatarFallback className="bg-gradient-to-br from-red-500 to-purple-600 text-white">
                    {user.name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{user.name}</p>
                  <p className="text-xs text-slate-500 truncate">@{user.nickname}</p>
                </div>
                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => handleShare(user)}
                    className="p-1.5 rounded-full hover:bg-slate-700"
                    title="Compartir"
                  >
                    <Share2 className="w-4 h-4 text-slate-400" />
                  </button>
                  <button 
                    onClick={() => removeUser(user.user_id)}
                    className="p-1.5 rounded-full hover:bg-red-500/20"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
                {user.status === 'online' && (
                  <div className="w-2 h-2 bg-green-500 rounded-full ml-2" />
                )}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

// Contact Lists Manager
const ContactListsManager = ({ onSelectUser }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [expandedList, setExpandedList] = useState(null);

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const res = await fetch(`${API}/api/user/contact-lists`, {
        headers: { ...getAuthHeaders() },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setLists(data);
      }
    } catch (err) {
      console.error("Error fetching contact lists:", err);
    }
    setLoading(false);
  };

  const createList = async () => {
    if (!newListName.trim()) return;
    try {
      const res = await fetch(`${API}/api/user/contact-lists`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ name: newListName, user_ids: [] })
      });
      if (res.ok) {
        const data = await res.json();
        setLists([...lists, data]);
        setNewListName("");
        setShowCreateDialog(false);
        toast.success("Lista creada");
      }
    } catch (err) {
      toast.error("Error al crear lista");
    }
  };

  const deleteList = async (listId) => {
    try {
      const res = await fetch(`${API}/api/user/contact-lists/${listId}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
        credentials: "include"
      });
      if (res.ok) {
        setLists(lists.filter(l => l.list_id !== listId));
        toast.success("Lista eliminada");
      }
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b border-slate-700/50 flex items-center justify-between">
        <h3 className="text-white font-medium">Mis Listas</h3>
        <Button 
          size="sm" 
          onClick={() => setShowCreateDialog(true)}
          className="bg-red-600 hover:bg-red-700 h-8"
        >
          <Plus className="w-4 h-4 mr-1" /> Nueva
        </Button>
      </div>

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-red-500" />
          </div>
        ) : lists.length === 0 ? (
          <div className="text-center py-8 px-4">
            <List className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No tienes listas de contactos</p>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="mt-4 bg-red-600 hover:bg-red-700"
            >
              Crear primera lista
            </Button>
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {lists.map(list => (
              <div key={list.list_id} className="bg-[#232e3c] rounded-lg overflow-hidden">
                <div 
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-[#2b3a4c]"
                  onClick={() => setExpandedList(expandedList === list.list_id ? null : list.list_id)}
                >
                  <div className="flex items-center">
                    <FolderPlus className="w-5 h-5 text-red-400 mr-2" />
                    <span className="text-white font-medium">{list.name}</span>
                    <span className="text-slate-500 text-sm ml-2">({list.users?.length || 0})</span>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); deleteList(list.list_id); }}
                    className="p-1 rounded hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
                {expandedList === list.list_id && list.users?.length > 0 && (
                  <div className="border-t border-slate-700/50 p-2 space-y-1">
                    {list.users.map(user => (
                      <div 
                        key={user.user_id}
                        className="flex items-center p-2 rounded hover:bg-[#2b5278] cursor-pointer"
                        onClick={() => onSelectUser?.(user)}
                      >
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback className="bg-red-600 text-white text-xs">{user.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <span className="ml-2 text-white text-sm">{user.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Create List Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Nueva lista de contactos</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Nombre de la lista"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            className="bg-[#242f3d] border-transparent text-white"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateDialog(false)} className="text-white">
              Cancelar
            </Button>
            <Button onClick={createList} disabled={!newListName.trim()} className="bg-red-600 hover:bg-red-700">
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Sidebar Sections Component
export const SidebarSections = ({ onSelectUser, onStartChat, platformDomain }) => {
  const [activeTab, setActiveTab] = useState("contacts");

  const tabs = [
    { id: "contacts", label: "Contactos", icon: Users },
    { id: "chats", label: "Chats", icon: MessageSquare },
    { id: "stores", label: "Tiendas", icon: Store },
    { id: "restaurants", label: "Restaurantes", icon: UtensilsCrossed },
    { id: "lists", label: "Listas", icon: List },
  ];

  return (
    <div className="h-full flex flex-col bg-[#17212b]">
      {/* Tab Navigation */}
      <div className="flex border-b border-slate-700/50 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 min-w-0 flex flex-col items-center py-3 px-2 transition-colors ${
              activeTab === tab.id 
                ? "text-red-400 border-b-2 border-red-400" 
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            <tab.icon className="w-5 h-5 mb-1" />
            <span className="text-xs truncate">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === "contacts" && (
          <CategorySection 
            category="contacts" 
            title="Contactos" 
            icon={Users}
            onSelectUser={onSelectUser}
            platformDomain={platformDomain}
          />
        )}
        {activeTab === "chats" && (
          <CategorySection 
            category="chats" 
            title="Chats Guardados" 
            icon={MessageSquare}
            onSelectUser={onStartChat}
            platformDomain={platformDomain}
          />
        )}
        {activeTab === "stores" && (
          <CategorySection 
            category="stores" 
            title="Mis Tiendas" 
            icon={Store}
            onSelectUser={onSelectUser}
            platformDomain={platformDomain}
          />
        )}
        {activeTab === "restaurants" && (
          <CategorySection 
            category="restaurants" 
            title="Mis Restaurantes" 
            icon={UtensilsCrossed}
            onSelectUser={onSelectUser}
            platformDomain={platformDomain}
          />
        )}
        {activeTab === "lists" && (
          <ContactListsManager onSelectUser={onSelectUser} />
        )}
      </div>
    </div>
  );
};

export default SidebarSections;
